/**
 * screens/performance/Performance.tsx — honest live learning.
 * Hero band, channel split, lifecycle ledger and a sortable, expandable
 * per-rule table. Rates count only confirmed-delivered, resolved suggestions.
 */
import React, { useCallback, useState } from 'react';
import { View, Pressable, ScrollView } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { LightColors as C, FontSize as FS, FontWeight as FW } from '../../theme/theme';
import { Txt, Row, Stack, Eyebrow } from '../../components/ui';
import { Card, CardHead, CardBody } from '../../components/card';
import Button from '../../components/button';
import Chip from '../../components/chip';
import Alert from '../../components/alert';
import Spinner from '../../components/spinner';
import Bar from '../../components/bar';
import Sparkline from '../../components/sparkline';
import Icon, { IconName } from '../../components/icon';
import { Page, PageHeader } from '../_layout';

import { Stats, RuleStats, StatsSort } from '../../schema/stats';
import { getStats } from '../../services/stats/stats';
import { motionMeta } from '../../domain/motions';
import { handleApiError } from '../../utils/errorHandler';
import { useApp } from '../../context/appContext';
import type { AppStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<AppStackParamList>;

const fmt = (n: number): string => (n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(n));
const pct = (x: number): string => `${Math.round(x * 100)}%`;

/** Premium section header: a soft icon tile + title (passed as CardHead children). */
function SectionHead({ icon, title }: { icon: IconName; title: string }) {
  return (
    <Row gap={10} style={{ alignItems: 'center' }}>
      <View style={{ width: 28, height: 28, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: C.paper2, borderWidth: 1, borderColor: C.line }}>
        <Icon name={icon} size={15} color={C.ink2} />
      </View>
      <Txt size={FS.md} weight={FW.semibold} color={C.ink}>
        {title}
      </Txt>
    </Row>
  );
}

function StatCard({ label, value, sub, tinted, primary }: { label: string; value: string; sub: string; tinted?: boolean; primary?: boolean }) {
  const dark = !!primary;
  return (
    <Card
      style={{
        flexGrow: 1,
        flexBasis: primary ? 200 : 160,
        minWidth: 150,
        ...(primary ? { backgroundColor: C.ink, borderColor: C.ink } : tinted ? { backgroundColor: C.paper2 } : {}),
      }}
    >
      <CardBody>
        <Txt size={FS.xs} weight={FW.semibold} color={dark ? C.paper3 : C.ink4} transform="uppercase" tracking={0.6}>
          {label}
        </Txt>
        <Txt size={dark ? 40 : 34} weight={FW.semibold} color={dark ? C.card : C.ink} tracking={-0.6} style={{ marginTop: 4 }}>
          {value}
        </Txt>
        <Txt size={FS.sm} color={dark ? C.paper3 : C.ink3}>
          {sub}
        </Txt>
      </CardBody>
    </Card>
  );
}

const COL = { chev: 26, motion: 110, num: 78, chan: 112, rate: 120, gap: 12 };
const TABLE_MIN = COL.chev + 190 + COL.motion + COL.num * 3 + COL.chan * 2 + COL.rate + COL.gap * 9;

function SortHeader({ label, active, dir, onPress, width }: { label: string; active: boolean; dir: 'asc' | 'desc'; onPress: () => void; width: number }) {
  return (
    <Pressable onPress={onPress} style={{ width }}>
      <Eyebrow>{`${label}${active ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}`}</Eyebrow>
    </Pressable>
  );
}

function PerfRow({ r, expanded, onToggle, onEdit }: { r: RuleStats; expanded: boolean; onToggle: () => void; onEdit: () => void }) {
  const m = motionMeta(r.motion);
  const hasData = r.accepted + r.declined > 0;
  const rateText = hasData ? pct(r.accept_rate) : '—';
  const rateColor = r.cold_start ? C.ink4 : C.ink;
  const stroke = r.cold_start ? C.ink4 : C.ink2;

  return (
    <View>
      <Pressable
        onPress={onToggle}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: COL.gap,
          paddingVertical: 11,
          paddingHorizontal: 12,
          borderBottomWidth: 1,
          borderBottomColor: C.line,
          backgroundColor: C.card,
        }}
      >
        <Txt size={13} color={C.ink4} style={{ width: COL.chev, textAlign: 'center' }}>
          {expanded ? '▾' : '▸'}
        </Txt>
        <Row style={{ flex: 1, minWidth: 0 }} gap={6}>
          <Txt size={FS.md} weight={FW.medium} color={C.ink} numberOfLines={1} style={{ flexShrink: 1 }}>
            {r.name}
          </Txt>
          {r.cold_start && hasData && <Chip label="learning" />}
        </Row>
        <View style={{ width: COL.motion }}>
          <Chip tone={m.tone} dot label={m.label} />
        </View>
        <Txt mono size={FS.md} color={C.ink2} style={{ width: COL.num }}>
          {fmt(r.delivered)}
        </Txt>
        <Txt mono size={FS.md} color={C.ink2} style={{ width: COL.num }}>
          {fmt(r.accepted)}
        </Txt>
        <Txt mono size={FS.md} color={C.ink3} style={{ width: COL.num }}>
          {fmt(r.declined)}
        </Txt>
        <Row style={{ width: COL.chan }} gap={7}>
          <Bar value={r.by_channel.voice.accept_rate} height={6} fillColor={C.ink3} style={{ width: 42 }} />
          <Txt mono size={13} color={C.ink3} style={{ minWidth: 32 }}>
            {hasData ? pct(r.by_channel.voice.accept_rate) : '—'}
          </Txt>
        </Row>
        <Row style={{ width: COL.chan }} gap={7}>
          <Bar value={r.by_channel.screen.accept_rate} height={6} fillColor={C.ink} style={{ width: 42 }} />
          <Txt mono size={13} color={C.ink2} style={{ minWidth: 32 }}>
            {hasData ? pct(r.by_channel.screen.accept_rate) : '—'}
          </Txt>
        </Row>
        <Row style={{ width: COL.rate }} gap={9}>
          <Sparkline data={r.trend} width={56} height={20} stroke={stroke} strokeWidth={1.5} />
          <Txt size={FS.md} weight={FW.semibold} color={rateColor} style={{ minWidth: 42 }}>
            {rateText}
          </Txt>
        </Row>
      </Pressable>

      {expanded && (
        <View style={{ backgroundColor: C.paper2, paddingVertical: 16, paddingHorizontal: 18, borderBottomWidth: 1, borderBottomColor: C.line }}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 18 }}>
            {/* Channel split */}
            <View style={{ flexGrow: 1, flexBasis: 280, minWidth: 240 }}>
              <Eyebrow style={{ marginBottom: 9 }}>Channel split</Eyebrow>
              <Stack gap={10}>
                <ChannelDetail label="Voice (SUKI)" stat={r.by_channel.voice} fill={C.ink3} />
                <ChannelDetail label="Screen" stat={r.by_channel.screen} fill={C.ink} />
                <Txt size={FS.xs} color={C.ink4} lineHeight={16}>
                  Voice caps at 1 suggestion, so its denominator is smaller — judged on its own rate, never penalised
                  against screen.
                </Txt>
              </Stack>
            </View>
            {/* Lifecycle ledger */}
            <View style={{ flexGrow: 1, flexBasis: 200, minWidth: 180 }}>
              <Eyebrow style={{ marginBottom: 9 }}>Lifecycle ledger</Eyebrow>
              <Stack gap={6}>
                <LedgerRow label="Offered" value={fmt(r.offered)} />
                <LedgerRow label="Delivered (confirmed)" value={fmt(r.delivered)} />
                <LedgerRow label="Delivery-unknown" value={fmt(r.delivery_unknown)} muted />
                <LedgerRow label="Accepted" value={fmt(r.accepted)} />
                <LedgerRow label="Declined" value={fmt(r.declined)} />
                <LedgerRow label="Ignored (excl.)" value={fmt(r.ignored)} muted />
              </Stack>
            </View>
            {/* Delivery confidence */}
            <View style={{ flexBasis: 170, flexGrow: 0 }}>
              <Eyebrow style={{ marginBottom: 9 }}>Delivery confidence</Eyebrow>
              <Txt size={30} weight={FW.semibold} color={C.ink} tracking={-0.6}>
                {pct(r.delivery_confidence)}
              </Txt>
              <Txt size={FS.sm} color={C.ink3} style={{ marginBottom: 14 }}>
                of offered were confirmed delivered
              </Txt>
              <Button label="Edit rule" size="sm" icon="pencil" onPress={onEdit} block />
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

const ChannelDetail: React.FC<{ label: string; stat: RuleStats['by_channel']['voice']; fill: string }> = ({ label, stat, fill }) => (
  <View>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
      <Txt size={13.5} weight={FW.medium} color={C.ink}>
        {label}
      </Txt>
      <Txt mono size={12.5} color={C.ink3}>
        {fmt(stat.accepted)}✓ / {fmt(stat.delivered)} del · {pct(stat.accept_rate)}
      </Txt>
    </View>
    <Bar value={stat.accept_rate} height={7} fillColor={fill} />
  </View>
);

const LedgerRow: React.FC<{ label: string; value: string; muted?: boolean }> = ({ label, value, muted }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
    <Txt size={13.5} color={muted ? C.ink4 : C.ink2}>
      {label}
    </Txt>
    <Txt mono size={13.5} color={muted ? C.ink4 : C.ink}>
      {value}
    </Txt>
  </View>
);

export default function Performance() {
  const nav = useNavigation<Nav>();
  const { vertical } = useApp();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<{ col: StatsSort; dir: 'asc' | 'desc' }>({ col: 'accept_rate', dir: 'desc' });
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStats(await getStats());
    } catch (e) {
      handleApiError(e, 'Could not load performance');
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load, vertical]),
  );

  const toggleSort = (col: StatsSort) =>
    setSort((s) => ({ col, dir: s.col === col && s.dir === 'desc' ? 'asc' : 'desc' }));

  if (loading || !stats) {
    return (
      <Page>
        <PageHeader icon="chart-line" eyebrow="Analytics" title="Performance" subtitle="Real acceptance, measured honestly." />
        <View style={{ paddingVertical: 56, alignItems: 'center' }}>
          <Spinner size={24} />
        </View>
      </Page>
    );
  }

  const a = stats.aggregate;
  const coldCount = stats.rules.filter((r) => r.cold_start).length;

  const sortVal = (r: RuleStats) => (sort.col === 'accept_rate' ? r.accept_rate : sort.col === 'delivered' ? r.delivered : r.accepted);
  const rows = [...stats.rules].sort((x, y) => (sortVal(x) - sortVal(y)) * (sort.dir === 'desc' ? -1 : 1));

  return (
    <Page>
      <PageHeader
        icon="chart-line"
        eyebrow="Analytics"
        title="Performance"
        subtitle="Real acceptance, measured honestly. Only confirmed-delivered, resolved suggestions count toward the rate — no rule is ever penalised for a line the guest may never have heard."
        style={{ marginBottom: 22 }}
      />

      {coldCount > 0 && (
        <Alert tone="info" iconSize={16} style={{ marginBottom: 16 }}>
          <Txt size={FS.md} color={C.info} lineHeight={20}>
            <Txt size={FS.md} weight={FW.semibold} color={C.info}>
              Still learning.{' '}
            </Txt>
            {coldCount} rule(s) are using your curated order until there’s enough signal. No fake numbers — picks are
            honest from turn one.
          </Txt>
        </Alert>
      )}

      {/* Hero band */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <StatCard label="Accept-rate" value={pct(a.accept_rate)} sub="accepted / (accepted + declined)" primary />
        <StatCard label="Delivered" value={fmt(a.delivered)} sub="confirmed shown" />
        <StatCard label="Accepted" value={fmt(a.accepted)} sub="explicit yes" />
        <StatCard label="Declined" value={fmt(a.declined)} sub="explicit no" />
        <StatCard label="Delivery confidence" value={pct(a.delivery_confidence)} sub={`${fmt(a.delivery_unknown)} delivery-unknown, excluded`} tinted />
      </View>

      {/* Channel split + lifecycle */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
        <Card style={{ flexGrow: 1, flexBasis: 260, minWidth: 260 }}>
          <CardHead right={<Txt size={FS.xs} color={C.ink4}>voice = 1 suggestion · screen = 2</Txt>}>
            <SectionHead icon="chart-line" title="Channel split" />
          </CardHead>
          <CardBody>
            <Stack gap={14}>
              <ChannelDetail label="Voice (SUKI)" stat={a.by_channel.voice} fill={C.ink3} />
              <ChannelDetail label="Screen" stat={a.by_channel.screen} fill={C.ink} />
              <View style={{ borderTopWidth: 1, borderTopColor: C.line, paddingTop: 10 }}>
                <Txt size={FS.xs} color={C.ink4} lineHeight={16}>
                  SUKI emits <Txt mono size={FS.xs} color={C.ink3}>suggestion.delivered</Txt> when it voices the line — so
                  voice learns instead of going blind.
                </Txt>
              </View>
            </Stack>
          </CardBody>
        </Card>

        <Card style={{ flexGrow: 1, flexBasis: 220, minWidth: 220 }}>
          <CardHead>
            <SectionHead icon="rules-list" title="Lifecycle ledger" />
          </CardHead>
          <CardBody>
            <Stack gap={9}>
              <LedgerRow label="Offered" value={fmt(a.offered)} />
              <LedgerRow label="Delivered" value={fmt(a.delivered)} />
              <LedgerRow label="Delivery-unknown" value={fmt(a.delivery_unknown)} muted />
              <LedgerRow label="Accepted" value={fmt(a.accepted)} />
              <LedgerRow label="Declined" value={fmt(a.declined)} />
              <LedgerRow label="Ignored" value={fmt(a.ignored)} muted />
            </Stack>
          </CardBody>
        </Card>
      </View>

      {/* Per-rule table */}
      <Card>
        <CardHead right={<Txt size={FS.xs} color={C.ink4}>click a header to sort · click a row to expand</Txt>}>
          <SectionHead icon="chart-line" title="Per-rule performance" />
        </CardHead>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ minWidth: TABLE_MIN, flexDirection: 'column' }}>
          {/* header */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: COL.gap, paddingVertical: 9, paddingHorizontal: 12, backgroundColor: C.paper2, borderBottomWidth: 1, borderBottomColor: C.line }}>
            <View style={{ width: COL.chev }} />
            <View style={{ flex: 1 }}>
              <Eyebrow>Rule</Eyebrow>
            </View>
            <View style={{ width: COL.motion }}>
              <Eyebrow>Motion</Eyebrow>
            </View>
            <SortHeader label="Delivered" active={sort.col === 'delivered'} dir={sort.dir} onPress={() => toggleSort('delivered')} width={COL.num} />
            <SortHeader label="Accepted" active={sort.col === 'accepted'} dir={sort.dir} onPress={() => toggleSort('accepted')} width={COL.num} />
            <View style={{ width: COL.num }}>
              <Eyebrow>Declined</Eyebrow>
            </View>
            <View style={{ width: COL.chan }}>
              <Eyebrow>Voice</Eyebrow>
            </View>
            <View style={{ width: COL.chan }}>
              <Eyebrow>Screen</Eyebrow>
            </View>
            <SortHeader label="Accept-rate" active={sort.col === 'accept_rate'} dir={sort.dir} onPress={() => toggleSort('accept_rate')} width={COL.rate} />
          </View>

          {rows.length === 0 ? (
            <View style={{ paddingVertical: 44, alignItems: 'center', gap: 4 }}>
              <Txt size={FS.md} weight={FW.medium} color={C.ink2}>
                No performance data yet
              </Txt>
              <Txt size={13} color={C.ink4} align="center">
                Numbers appear here once customers start seeing and accepting your live suggestions.
              </Txt>
            </View>
          ) : (
            rows.map((r) => (
              <PerfRow
                key={r.rule_id}
                r={r}
                expanded={expanded === r.rule_id}
                onToggle={() => setExpanded((id) => (id === r.rule_id ? null : r.rule_id))}
                onEdit={() => nav.navigate('RuleBuilder', { ruleId: r.rule_id })}
              />
            ))
          )}
        </ScrollView>
      </Card>
    </Page>
  );
}
